import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomTags from "../../components/CustomTags";
import AudioFileDropZone from "./AudioFileDropZone";
import AudioService, { AUDIO_CATEGORIES, AUDIO_SOURCES, AUDIO_SOURCE_LABELS } from "./AudioService";
import { ADMIN_AUDIO_LIST_PATH } from "./audioRoutes";
import {
    buildAudioDownloadFilename,
    downloadAudioAsset,
    formatAudioBytes,
    getAudioUploadValidationError,
    normalizeTagLabel,
    normalizeTagsArray,
    tagsToCommaInput,
} from "./audioUtils";

export default function AdminAudioFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCreate = !id;

    const [loading, setLoading] = useState(!isCreate);
    const [saving, setSaving] = useState(false);
    const [existing, setExisting] = useState(null);

    const [name, setName] = useState("");
    const [category, setCategory] = useState(AUDIO_CATEGORIES[0] || "BGM");
    const [source, setSource] = useState("");
    const [tags, setTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [distinctTags, setDistinctTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [tagsTouched, setTagsTouched] = useState(false);
    const [enable, setEnable] = useState(false);
    const [file, setFile] = useState(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTagsLoading(true);
            const result = await AudioService.listDistinctTags();
            if (!cancelled) {
                const rows = result.success && Array.isArray(result.data) ? result.data : [];
                setDistinctTags(rows);
                setTagOptions(rows.map((row) => row.tag));
                setTagsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isCreate) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            const result = await AudioService.getById(id);
            if (cancelled) return;
            setLoading(false);
            if (result.success && result.data) {
                const d = result.data;
                setExisting(d);
                setName(d.name || "");
                setCategory(d.category || AUDIO_CATEGORIES[0]);
                setSource(d.source || "");
                setTags(normalizeTagsArray(d.tags));
                setEnable(Boolean(d.enable));
            } else {
                setSeverity("error");
                setMessage(result.error || "Failed to load audio");
                setOpenSnackbar(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, isCreate]);

    const handleSnack = (sev, msg) => {
        setSeverity(sev);
        setMessage(msg);
        setOpenSnackbar(true);
    };

    const handleDownload = async () => {
        if (!existing?.url || downloading) return;
        setDownloading(true);
        try {
            await downloadAudioAsset(existing);
        } catch (e) {
            handleSnack("error", e?.message || "Failed to download audio");
        } finally {
            setDownloading(false);
        }
    };

    const handleDistinctTagChipClick = (tag) => {
        const label = normalizeTagLabel(tag);
        if (!label) return;
        setTagsTouched(true);
        setTags((prev) => {
            const key = label.toLowerCase();
            if (prev.some((t) => String(t).toLowerCase() === key)) return prev;
            return normalizeTagsArray([...prev, label]);
        });
    };

    const validate = () => {
        if (!name.trim()) {
            handleSnack("error", "Name is required");
            return false;
        }
        if (!category) {
            handleSnack("error", "Category is required");
            return false;
        }
        if (isCreate && !file) {
            handleSnack("error", "Audio file is required");
            return false;
        }
        const fileErr = file ? getAudioUploadValidationError(file) : null;
        if (fileErr) {
            handleSnack("error", fileErr);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        const tagsPayload = tagsToCommaInput(tags);
        const sourcePayload = String(source || "").trim();
        const result = isCreate
            ? await AudioService.create({
                  file,
                  name,
                  category,
                  tags: tagsPayload,
                  enable,
                  source: sourcePayload,
              })
            : await AudioService.update(id, {
                  file: file || undefined,
                  name,
                  category,
                  tags: tagsPayload,
                  tagsTouched: isCreate || tagsTouched,
                  enable,
                  includeEnable: true,
                  source: sourcePayload,
                  sourceTouched: true,
              });
        setSaving(false);
        if (result.success) {
            navigate(ADMIN_AUDIO_LIST_PATH, {
                state: { message: isCreate ? "Audio uploaded" : "Audio updated" },
            });
        } else {
            handleSnack("error", result.error || "Save failed");
        }
    };

    const handleDelete = async () => {
        setOpenDeleteDialog(false);
        setSaving(true);
        const result = await AudioService.delete(id);
        setSaving(false);
        if (result.success) {
            navigate(ADMIN_AUDIO_LIST_PATH, { state: { message: "Audio deleted" } });
        } else {
            handleSnack("error", result.error || "Delete failed");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 720 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <IconButton aria-label="Back to list" onClick={() => navigate(ADMIN_AUDIO_LIST_PATH)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" component="h1">
                    {isCreate ? "Upload audio" : "Edit audio"}
                </Typography>
            </Stack>

            <Stack spacing={2.5}>
                <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    fullWidth
                    disabled={saving}
                />

                <FormControl fullWidth required disabled={saving}>
                    <InputLabel id="audio-category-label">Category</InputLabel>
                    <Select
                        labelId="audio-category-label"
                        label="Category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        {AUDIO_CATEGORIES.map((c) => (
                            <MenuItem key={c} value={c}>
                                {c}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    freeSolo
                    options={AUDIO_SOURCE_LABELS}
                    value={source}
                    onChange={(_e, next) => setSource(next == null ? "" : String(next))}
                    onInputChange={(_e, next) => setSource(next == null ? "" : String(next))}
                    disabled={saving}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Source"
                            placeholder="Where the file came from"
                            helperText={
                                (() => {
                                    const known = AUDIO_SOURCES.find(
                                        (s) =>
                                            s.label.toLowerCase() ===
                                            String(source || "").trim().toLowerCase()
                                    );
                                    if (known) {
                                        if (known.url) {
                                            return (
                                                <>
                                                    {known.hint} —{" "}
                                                    <Link
                                                        href={known.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {String(known.url).replace(/^https?:\/\//, "")}
                                                    </Link>
                                                </>
                                            );
                                        }
                                        return known.hint || "";
                                    }
                                    return "Optional. Pick a known site (e.g. Incompetech, Mixkit) or type another origin.";
                                })()
                            }
                        />
                    )}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={enable}
                            onChange={(e) => setEnable(e.target.checked)}
                            disabled={saving}
                        />
                    }
                    label="Enabled"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1.5 }}>
                    When enabled, this audio can be used in the app. New uploads default to off until you turn this on.
                </Typography>

                <Box>
                    <CustomTags
                        value={tags}
                        onChange={(e, newTags) => {
                            setTags(newTags);
                            setTagsTouched(true);
                        }}
                        options={tagOptions}
                        loading={tagsLoading}
                        label="Tags"
                        placeholder="Search or add tags…"
                        helperText="Pick an existing tag or type a new one and press Enter. Leave empty for no tags."
                        disabled={saving}
                        margin="none"
                    />
                    {tagsLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                                Loading tags…
                            </Typography>
                        </Box>
                    ) : distinctTags.length > 0 ? (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                Tags in library — click to add
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {distinctTags.map((row) => {
                                    const label = row.tag;
                                    const selected = tags.some(
                                        (t) => String(t).toLowerCase() === label.toLowerCase()
                                    );
                                    return (
                                        <Chip
                                            key={label}
                                            label={`${label} (${row.count})`}
                                            size="small"
                                            clickable
                                            disabled={saving}
                                            color={selected ? "primary" : "default"}
                                            variant={selected ? "filled" : "outlined"}
                                            onClick={() => handleDistinctTagChipClick(label)}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : null}
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Audio file {isCreate ? "(required)" : "(optional — leave empty to keep current)"}
                    </Typography>
                    <AudioFileDropZone
                        file={file}
                        onFileChange={setFile}
                        disabled={saving}
                        required={isCreate}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Upload MP3, WAV, or Apple Core Audio Format (CAF).
                    </Typography>
                </Box>

                {!isCreate && existing?.url && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Current audio
                        </Typography>
                        {existing.originalFilename ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Original file: {existing.originalFilename}
                            </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {formatAudioBytes(existing.sizeBytes)}
                            {existing.s3Key ? ` · ${existing.s3Key}` : ""}
                        </Typography>
                        <Box component="audio" controls src={existing.url} sx={{ width: "100%", maxWidth: 480 }} />
                        <Button
                            sx={{ mt: 1.5 }}
                            variant="outlined"
                            size="small"
                            startIcon={
                                downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                            }
                            disabled={downloading}
                            onClick={handleDownload}
                        >
                            {downloading
                                ? "Downloading…"
                                : `Download as ${buildAudioDownloadFilename(existing)}`}
                        </Button>
                    </Box>
                )}

                <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                    <Button type="submit" variant="contained" disabled={saving}>
                        {saving ? "Saving…" : isCreate ? "Upload" : "Save"}
                    </Button>
                    <Button variant="outlined" disabled={saving} onClick={() => navigate(ADMIN_AUDIO_LIST_PATH)}>
                        Cancel
                    </Button>
                    {!isCreate && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            disabled={saving}
                            onClick={() => setOpenDeleteDialog(true)}
                            sx={{ ml: "auto" }}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
            </Stack>

            <ConfirmDialog
                open={openDeleteDialog}
                setOpen={setOpenDeleteDialog}
                title="Delete audio"
                targetName={existing?.name || name}
                message="Delete this audio asset? This cannot be undone."
                callback={handleDelete}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
